import {Component} from '@angular/core';
import { ChargeAreaDetailsMarkerDto } from '../../../../domain/map/chargeAreaDetailsMarker';
import { ChargeAreaMarkerDto } from '../../../../domain/map/chargeAreaMarker';
import { FrontMapMarkerHttpService } from '../../../../services/http/mapMarkerHttp.service';
import { MapMarkerDetailsLazyCategory } from '../../../../domain/map/chargeAreaDetailsMarker';
import { StationDto } from '../../../../domain/infra/station/station';
import { UserAction } from '../../../../domain/infra/userAction';
import {ActivatedRoute} from '@angular/router';
import {NavController, ViewWillEnter} from '@ionic/angular';
import { GhostComponent } from '../../../../utils/ghostComponent';
import { IziviaToastService } from '../../../../services/common/iziviaToast.service';
import { NewChargeContext } from '../../../../services/business/newChargeContext.service';
import { NewChargeStepType } from '../../../../domain/headerStep';
import {StationSearcherService} from '../../../../services/business/stationSearcher.service';
import {PaynowStationContextService} from '../../../../services/business/paynowStationContext.service';
import {StationUtilsService} from '../../../../services/utils/stationUtils.service';

@Component({
    selector: 'station-choice-view',
    templateUrl: './stationChoice.view.html',
    styleUrls: ['./stationChoice.view.scss']
})
export class StationChoiceView extends GhostComponent implements ViewWillEnter {
    newChargeContext: NewChargeContext;
    step: NewChargeStepType = NewChargeStepType.STATION_CHOICE;
    stationAlias: string;
    isSearchingStation: boolean;
    nearestChargeAreas: Array<SelectableChargeArea>;
    locationUnavailable: boolean;

    constructor(private readonly route: ActivatedRoute,
        private readonly navController: NavController,
        private readonly toastService: IziviaToastService,
        private readonly stationSearcherService: StationSearcherService,
        private readonly chargeAreaService: FrontMapMarkerHttpService,
        private readonly paynowStationContextService: PaynowStationContextService,
        private readonly stationUtilsService: StationUtilsService) {
        super();
    }

    ionViewWillEnter() {
        this.ghostMode = true;
        this.newChargeContext = this.route.snapshot.data.newChargeContext;

        if (this.newChargeContext.isRetryOnOtherStation) {
            this.stationAlias = null;
            this.newChargeContext.isRetryOnOtherStation = false;
        }

        if (navigator?.geolocation) {
            navigator.geolocation.getCurrentPosition(
                position => this.chargeAreaService.searchNearestAroundMapMarkers({
                    referenceLat: position.coords.latitude,
                    referenceLng: position.coords.longitude,
                    distanceMaxInKm: 10,
                    limit: 3,
                    categories: [
                        MapMarkerDetailsLazyCategory.STATION
                    ],
                    stationCategories: this.stationUtilsService.newChargeStationLazyCategories,
                    userActions: [UserAction.BUY_OTP],
                    availableOnly: true
                }).subscribe(chargeAreas => {
                    this.nearestChargeAreas = chargeAreas;
                    this.ghostMode = false;
                }),
                () => {
                    this.handleUnavailableLocation();
                });
        } else {
            this.handleUnavailableLocation();
        }
    }

    private handleUnavailableLocation() {
        this.toastService.showWarning('station.error.location');
        this.locationUnavailable = true;
        this.nearestChargeAreas = [];
        this.ghostMode = false;
    }

    goToNextSlide() {
        this.isSearchingStation = true;

        this.stationSearcherService.findByAlias(this.stationAlias)
            .subscribe(
                station => {
                    this.fillNewChargeContextThenNavigateToPlugTypeChoiceStep(
                        station,
                        () => this.isSearchingStation = false
                    );
                },
                _ => this.isSearchingStation = false
            );
    }

    private fillNewChargeContextThenNavigateToPlugTypeChoiceStep(
        station: StationDto,
        onFinally: (() => void) | undefined = undefined
    ) {
        this.newChargeContext.stationId = station.id;
        this.newChargeContext.isReservable = station.currentUserActions.includes(UserAction.RESERVE);
        this.newChargeContext.otpSession.stationRef = station.id;

        this.paynowStationContextService.set({ contextId: this.newChargeContext.id,  station });

        this.navController
            .navigateForward(
                [`/app/new-charge/station/${station.id}/plug-type-choice`],
                { queryParams: this.newChargeContext.toQueryParam() }
            )
            .finally(onFinally);
    }

    goToHome() {
        this.navController.navigateRoot(['/home'], {
            replaceUrl: true
        });
    }

    canGoNext(): boolean {
        return !this.isSearchingStation && !!this.stationAlias && this.stationAlias.length > 0;
    }

    selectChargeArea(chargeArea: SelectableChargeArea): void {
        this.nearestChargeAreas
            .filter(value => value.selected && value.id !== chargeArea.id)
            .map(value => value.selected = false);
        chargeArea.selected = !chargeArea.selected;
    }

    selectStation(station: StationDto) {
        this.fillNewChargeContextThenNavigateToPlugTypeChoiceStep(station);
    }

    generateGhost(): void {
        this.nearestChargeAreas = [
            {'@type': 'chargeAreaDetails'},
            {'@type': 'chargeAreaDetails'},
            {'@type': 'chargeAreaDetails'}
        ];
    }

    trackByChargeAreaId(index: number, chargeArea: ChargeAreaMarkerDto) {
        return chargeArea?.id ?? index;
    }

    trackByStationId(index: number, station: StationDto) {
        return station?.id ?? index;
    }
}

interface SelectableChargeArea extends ChargeAreaDetailsMarkerDto {
    selected?: boolean;
}
